import { useState } from 'react';
import '../../styles/onboardingFlow/forgotPassword/forgotPassword.scss';
import { useNavigate } from 'react-router-dom';
import { applicationWWWXRLEncodedBodyBuilder } from '../../lib/utils/body-builder';
import { RequestInfo } from '../../interfaces/RequestInfo';
import { asyncHandler } from '../../lib/api/async-handler';
import { useTranslation } from 'react-i18next';
import MainSquareButton from '../../ui/buttons/MainSquareButton';
import { useDispatch } from 'react-redux';
import { submitLogToBugsnag } from '../../lib/api/log';
import AINormalInput from '../../ui/inputs/AINormalInput';
import { updateProfile } from '../../store/reducers/userSlice';
import { FORGOT_PASSWORD_ENDPOINT } from '../../lib/constants/central-api';
import useTitle from '../../customHooks/useTitle';
import adupLogoSet from '../../assets/onboarding/common/adup-logo-set.png';
import VerifyOTP from './VerifyOTP';
import OnboardingMobileLogoPanel from '../onboardingComponents/OnboardingMobileLogoPanel';
import OnboardingBackButton from '../onboardingComponents/OnboardingBackButton';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  useTitle(t('ForgotPassword'));
  const [email, setEmail] = useState('');
  const dispatch = useDispatch();
  const isDisabled = email === '' ? true : false;
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [loading, setLoading] = useState(false);

  async function onSubmitHandler(e: any) {
    e.preventDefault();
    setLoading(true);
    try {
      const requestInfo: RequestInfo = {
        url: FORGOT_PASSWORD_ENDPOINT,
        method: 'POST',
        body: applicationWWWXRLEncodedBodyBuilder({
          email: email,
        }),
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/x-www-form-urlencoded',
        },
      };

      const actionType = 'FORGOT_PASSWORD';
      const customMsg = t('ForgotPasswordSuccess-msg');

      const res: any = await asyncHandler(requestInfo, actionType, customMsg);

      const response = res.data;

      if (res.actionType === actionType && response.success) {
        setIsEmailSent(true);
        setLoading(false);
        dispatch(
          updateProfile({
            email: email,
          })
        );
      } else {
        setLoading(false);
      }
    } catch (exception) {
      setLoading(false);
      console.log('Error during authentication request');
      submitLogToBugsnag('error', `${exception}`);
    }
  }

  if (isEmailSent) {
    return <VerifyOTP setIsEmailSent={setIsEmailSent} />;
  }

  return (
    <div className='onboarding-forgot-password'>
      {/* Logo panel (mobile only) */}
      <OnboardingMobileLogoPanel />

      <div className='onboarding-forgot-password__container'>
        {/* Back button (mobile only) */}
        <OnboardingBackButton
          onClick={() => navigate(-1)}
          aria-label={t('GoBack')}
        />

        {/* Logo (desktop only; hidden on mobile when logo panel is shown) */}
        <div className='onboarding-forgot-password__logo'>
          <img src={adupLogoSet} alt='AdUp Logo' />
        </div>

        {/* Heading */}
        <div className='onboarding-forgot-password__header'>
          <h1>{t('ForgotPassword')}</h1>
          <p>{t('ForgotPasswordSubText-new-ui')}</p>
        </div>

        {/* Form */}
        <form
          onSubmit={onSubmitHandler}
          className='onboarding-forgot-password__form'
        >
          {/* Email Input */}
          <div className='onboarding-forgot-password__field'>
            <AINormalInput
              id='forgot-password-form-email'
              value={email}
              type='email'
              changeListeners={[(e) => setEmail(e.target.value)]}
              placeholder={t('EmailAddress')}
              innerPlaceholder={'youremail@example.com'}
              required={true}
              pattern='^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}(?:\.[a-zA-Z]{2,})?$'
            />
          </div>

          {/* Send Reset OTP Button */}
          <div className='onboarding-forgot-password__button'>
            <MainSquareButton
              value={t('SendResetOTP')}
              type='submit'
              disabled={isDisabled}
              loading={loading}
              classes='onboarding-forgot-password__continue-btn'
            />
          </div>

          {/* Sign In Link */}
          <div className='onboarding-forgot-password__signin'>
            <span>
              {t('RememberYourPassword')}{' '}
              <span
                className='onboarding-forgot-password__signin-link'
                onClick={() => navigate('/login')}
              >
                {t('SignIn')}
              </span>
            </span>
          </div>
        </form>
      </div>

      {/* Copyright */}
      <div className='onboarding-forgot-password__copyright'>
        <span>© AdUp {new Date().getFullYear()}</span>
      </div>
    </div>
  );
};

export default ForgotPassword;
